//@ts-strict-ignore
import React, { CSSProperties, forwardRef, useEffect, useMemo, useState } from 'react';

import { ITextFieldProps, TextField } from '@fluentui/react/lib/TextField';
import getValue from '../connectedForm/utils/getValue';
import InputBlock from '../connectedForm/InputBlock';
import InputLabel, { IInputLabelProps } from '../connectedForm/InputLabel';
import InputGroup from '../connectedForm/InputGroup';
import { useSymbolFromCurrency } from '../../utils/CurrencyUtil';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/src/components/TextField/TextField.types';
import { IStyleFunctionOrObject } from '@fluentui/react';
import isFunction from '../react-data-grid/utils/isFunction';

export type IFluentTextFieldProps = ITextFieldProps & {
  validationErrors?: any;
  customValidationErrors?: any;
  inputCols?: number;
  labelCols?: number;
  labelStyle?: { label: CSSProperties; root: CSSProperties };
  onChange?: (value) => void;
  onBlur?: (value) => void;
  type?: 'currency' | 'baseCurrency' | undefined;
  numeric?: boolean;
  readOnly?: boolean;
  decimals?: number; //How many decimal places to display numeric/currency values to.
  testId?: string;
  styles?: IStyleFunctionOrObject<ITextFieldStyleProps, ITextFieldStyles>;
  labelProps?: IInputLabelProps;
};

const useFormattedValue = (
  initialValue: any,
  { numeric = false, decimals = 2, type }: { numeric: boolean; decimals?: number; type?: 'currency' | 'baseCurrency' }
) => {
  const [value, setValue] = useState(initialValue ?? '');

  useEffect(() => {
    setValue(initialValue ?? '');
  }, [initialValue]);

  const formattedValue = useMemo(() => {
    if (numeric || type === 'currency' || type === 'baseCurrency') {
      if (!isNaN(value as any)) {
        return value?.toFixed?.(decimals) || value;
      }
      return Number.parseFloat(value as string).toFixed(decimals);
    }
    return value;
  }, [numeric, type, value, decimals]);

  return [formattedValue, setValue];
};

/**
 * Intended as a drop-in replacement for BlurTextField when additional functionality is required (Such as 'suffix' section).
 * Uses the Fluent UI text field as a base.
 * Work-in-progress - Do not expect all features of BlurTextField to be supported yet, now for styling to be exactly the same.
 */
export default forwardRef(
  (
    {
      value: initialValue,
      prefix,
      suffix,
      onChange,
      onBlur,
      onFocus,
      styles,
      description,
      label,
      labelStyle,
      inputCols,
      labelCols,
      validationErrors,
      customValidationErrors,
      type,
      readOnly,
      numeric = false, //Replaces the "datatype" prop on BlurTextField, which is only ever used to tag 'numeric'.
      decimals,
      placeholder,
      testId,
      labelProps,
    }: IFluentTextFieldProps,
    ref: any
  ) => {
    const [value, setValue] = useFormattedValue(initialValue, { numeric, decimals, type });
    const currencySymbol = useSymbolFromCurrency(type, value);

    const processValue = (event) => {
      const value = getValue(event);
      setValue(value || '');
      return value;
    };

    return (
      <InputBlock flexBox={true}>
        <InputLabel
          {...labelProps}
          label={labelProps?.label || label}
          cols={labelProps?.cols || labelCols}
          styles={labelProps?.styles || labelStyle}
        />
        <InputGroup flexBox={true} cols={label ? inputCols : 1} withLabel={!!label}>
          <TextField
            componentRef={ref}
            borderless={true}
            styles={
              isFunction(styles)
                ? styles
                : {
                    field: styles?.field || {
                      padding: '0px',
                      display: 'flex',
                      width: 'inherit',
                      alignSelf: 'baseline',
                      marginLeft: '3px',
                      marginTop: '5px',
                    },
                    suffix: styles?.suffix || {
                      background: 'none',
                      alignSelf: 'baseline',
                    },
                  }
            }
            onChange={(event) => {
              const newValue = processValue(event);
              onChange?.(newValue);
            }}
            onBlur={(event) => {
              const newValue = processValue(event);
              onBlur?.(newValue);
            }}
            onFocus={onFocus}
            value={value}
            prefix={prefix ?? currencySymbol ?? undefined}
            suffix={suffix}
            errorMessage={validationErrors || customValidationErrors}
            description={description}
            readOnly={readOnly}
            placeholder={placeholder}
            data-testid={testId}
          />
        </InputGroup>
      </InputBlock>
    );
  }
);
